import React, { useState, useRef, useContext } from "react";
import VN from "../components/VN";
import {
  Card,
  CardContent,
  Button,
  Container,
  Grid,
  LinearProgress,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import { UserDataContext } from "../components/UserDataContext";

const COLOR_OPTIONS = [
  { name: "CZERWONY", value: "#DC2626" },
  { name: "NIEBIESKI", value: "#2563EB" },
  { name: "ZIELONY", value: "#16A34A" },
  { name: "ŻÓŁTY", value: "#FFFF00" },
  { name: "BRĄZOWY", value: "#b46511" },
  { name: "CZARNY", value: "#000000" },
];

const TOTAL_TRIALS = 10;

export default function StroopTest() {
  const [gameState, setGameState] = useState("idle"); // 'idle' | 'running' | 'finished'
  const [currentWord, setCurrentWord] = useState(null);
  const [currentInk, setCurrentInk] = useState(null);
  const [trialCount, setTrialCount] = useState(0);
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [reactionTimes, setReactionTimes] = useState([]);
  const { userData } = useContext(UserDataContext);
  const startTimeRef = useRef(null);

  const startTest = () => {
    setScore({ correct: 0, wrong: 0 });
    setReactionTimes([]);
    setTrialCount(0);
    setGameState("running");
    nextTrial();
  };

  const nextTrial = () => {
    const randomWord =
      COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)];
    let randomInk =
      COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)];

    if (Math.random() < 0.6) {
      while (randomInk.name === randomWord.name) {
        randomInk =
          COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)];
      }
    }

    setCurrentWord(randomWord.name);
    setCurrentInk(randomInk);
    startTimeRef.current = Date.now();
  };

  const handleAnswer = (selectedColorName) => {
    if (gameState !== "running") return;

    const reactionTime = Date.now() - startTimeRef.current;
    const isCorrect = selectedColorName === currentInk.name;

    setReactionTimes((prev) => [...prev, reactionTime]);
    setScore((prev) => ({
      correct: isCorrect ? prev.correct + 1 : prev.correct,
      wrong: !isCorrect ? prev.wrong + 1 : prev.wrong,
    }));

    const nextCount = trialCount + 1;
    setTrialCount(nextCount);

    if (nextCount < TOTAL_TRIALS) {
      nextTrial();
    } else {
      setGameState("finished");
    }
  };

  const avgReactionTime = reactionTimes.length
    ? Math.round(
        reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length,
      )
    : 0;

  return (
    <div>
      <VN userData={userData} />
      <Container sx={{ py: 5 }}>
        <Card
          elevation={1}
          sx={{
            backgroundColor: "#F8FAFC",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              textAlign: "center",
              pt: 4,
              pb: 2,
              px: 3,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: "1.75rem", mb: 1 }}
            >
              Ocena Sprawności Poznawczej
            </Typography>
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: "bold", color: "text.primary", mb: 1 }}
            >
              Test Stroopa
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Wskazuj <strong>KOLOR CZCIONKI</strong>, jakim napisane jest
              słowo, ignorując jego treść.
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {gameState === "idle" && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  Test składa się z <strong>{TOTAL_TRIALS} prób</strong>.
                  Mierzymy Twój czas reakcji oraz poprawność odpowiedzi. Staraj
                  się odpowiadać jak najszybciej!
                </Typography>
                <Button variant="contained" size="large" onClick={startTest}>
                  Rozpocznij test
                </Button>
              </Box>
            )}

            {gameState === "running" && (
              <Box>
                {/* Pasek postępu */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Postęp
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {trialCount + 1} z {TOTAL_TRIALS}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={((trialCount + 1) / TOTAL_TRIALS) * 100}
                    color="info"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {/* Pole z wyświetlanym słowem */}
                <Paper
                  elevation={1}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    my: 4,
                    height: 160,

                    border: "1px solid #E2E8F0",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: currentInk.value,
                      fontSize: "3rem",
                      fontWeight: 800,
                      letterSpacing: "2px",
                      userSelect: "none",
                    }}
                  >
                    {currentWord}
                  </Typography>
                </Paper>

                {/* Opcje odpowiedzi */}
                <Grid container spacing={2}>
                  {COLOR_OPTIONS.map((color) => (
                    <Grid item xs={6} key={color.name}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleAnswer(color.name)}
                        sx={{
                          py: 2,
                          fontWeight: "bold",
                          backgroundColor: color.value,
                          color: "#FFFFFF",
                          fontSize: "1.1rem",

                          "&:hover": {
                            backgroundColor: color.value,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {color.name}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {gameState === "finished" && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 3, color: "text.primary" }}
                >
                  Wyniki testu
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 3, textAlign: "center" }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Poprawne
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", color: "success.main" }}
                      >
                        {score.correct}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 3, textAlign: "center" }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Błędne
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", color: "error.main" }}
                      >
                        {score.wrong}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 3, textAlign: "center" }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Śr. czas
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", color: "primary.main" }}
                      >
                        {avgReactionTime}{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          ms
                        </Typography>
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Button variant="outlined" size="large" onClick={startTest}>
                  Spróbuj ponownie
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}
